import {
  ButtonAction,
  ButtonElement,
  ButtonInteractionType,
  CloseActionType,
} from "@redotech/redo-model/content-builder/element-schema";
import { memo, useCallback, useMemo } from "react";
import { BrandKitButton } from "../../brand-kit-button";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { executeCloseActions } from "../spotlight/close-action-executor";
import { getBaseElementStyles } from "./base-element-styles";
import { FormElementProps } from "./index";

interface FormButtonProps
  extends Pick<
    FormElementProps<ButtonElement>,
    | "element"
    | "brandKit"
    | "brandKitOverrides"
    | "onClose"
    | "submitting"
    | "closeActionContext"
    | "isBuilder"
    | "onWillSubmit"
  > {}

export const FormButton = memo(function FormButton({
  element,
  brandKit,
  brandKitOverrides,
  submitting,
  onClose,
  closeActionContext,
  isBuilder,
  onWillSubmit,
}: FormButtonProps) {
  const interaction = element.interaction;

  const shouldHide =
    element.action === ButtonAction.CLOSE &&
    !onClose &&
    interaction?.interactionType !== ButtonInteractionType.CLOSE_AND_ACTION &&
    interaction?.interactionType !== ButtonInteractionType.OPEN_LINK;

  const handleClick = useCallback(async () => {
    if (interaction) {
      switch (interaction.interactionType) {
        case ButtonInteractionType.CLOSE:
          onClose?.();
          return;
        case ButtonInteractionType.CLOSE_AND_ACTION:
          await executeCloseActions(
            interaction.actions,
            closeActionContext ?? {},
          );
          onClose?.();
          return;
        case ButtonInteractionType.OPEN_LINK:
          if (interaction.openInNewTab) {
            window.open(interaction.url, "_blank", "noopener,noreferrer");
          } else {
            window.location.href = interaction.url;
          }
          return;
        case ButtonInteractionType.GO_TO_NEXT:
        case ButtonInteractionType.GO_TO_STEP:
          return;
      }
    }

    if (element.action === ButtonAction.SUBMIT_AND_GO_TO_URL) {
      onWillSubmit?.(element.destinationUrl);
      return;
    }

    if (onClose && element.action === ButtonAction.CLOSE) {
      onClose();
    }
  }, [
    interaction,
    onClose,
    closeActionContext,
    element.action,
    element.destinationUrl,
    onWillSubmit,
  ]);

  const subtext = useMemo(() => {
    if (!closeActionContext?.buttonSubtext) {
      return undefined;
    }
    if (
      interaction?.interactionType !== ButtonInteractionType.CLOSE_AND_ACTION
    ) {
      return undefined;
    }
    const hasCheckoutAction = interaction.actions.some(
      (a) => a.type === CloseActionType.OPEN_CHECKOUT,
    );
    return hasCheckoutAction ? closeActionContext.buttonSubtext : undefined;
  }, [closeActionContext?.buttonSubtext, interaction]);

  if (shouldHide) {
    return null;
  }

  const hasBaseStyles =
    element.backgroundColor !== undefined ||
    element.borderWidth !== undefined ||
    element.padding !== undefined;

  const isSubmitButton =
    interaction?.interactionType === ButtonInteractionType.GO_TO_NEXT ||
    interaction?.interactionType === ButtonInteractionType.GO_TO_STEP ||
    element.action === ButtonAction.SUBMIT_AND_ADVANCE ||
    element.action === ButtonAction.SUBMIT_AND_GO_TO_URL;

  const buttonElement = (
    <BrandKitButton
      brandKit={brandKit}
      disabled={isBuilder}
      elementOverride={element.styleOverride}
      globalOverride={
        brandKitOverrides?.buttons.enabled
          ? brandKitOverrides?.buttons
          : undefined
      }
      hierarchy={element.hierarchy}
      onClick={handleClick}
      pending={submitting}
      text={element.text}
      type={isSubmitButton ? "submit" : "button"}
    />
  );

  const content = subtext ? (
    <Flex dir="column" gap="xs" style={{ width: "100%" }}>
      {buttonElement}
      <Flex justify="center" style={{ width: "100%" }}>
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {subtext}
        </Text>
      </Flex>
    </Flex>
  ) : (
    buttonElement
  );

  if (!hasBaseStyles) {
    return content;
  }

  return (
    <div style={{ ...getBaseElementStyles(element), width: "100%" }}>
      {content}
    </div>
  );
});
